import { useState, useEffect, useMemo, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { Plus, Building2, Upload, Users, Edit, Edit2, Trash2, Image, UserPlus, Shield, User, FileText, Mail, Calendar, Settings, Filter, Tag, BarChart3, Download, AlertTriangle, FileUp, ToggleLeft, ToggleRight, Power, PowerOff, KeyRound, Eye, EyeOff, Copy } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const EMAIL_TEMPLATE_TYPES = [
  { id: 'candidate_invitation', name: 'Candidate Invitation', description: 'Sent when candidate is invited to portal', frequency: 'One-time' },
  { id: 'candidate_reminder', name: 'Reminder for Completion', description: 'Daily reminder until submission', frequency: 'Daily till submit' },
  { id: 'data_submission_notification', name: 'Data Submission', description: 'When candidate submits their data', frequency: 'One-time' },
  { id: 'insufficiency_raised', name: 'Insufficiency Raised', description: 'When insufficiency is raised by QC/Agent', frequency: 'On raise' },
  { id: 'insufficiency_cleared', name: 'Insufficiency Cleared', description: 'When insufficiency is cleared', frequency: 'On clear' },
  { id: 'consolidated_insuff_mis', name: 'Insufficiency MIS', description: 'Weekly consolidated insufficiency report', frequency: 'Weekly' },
  { id: 'consolidated_open_cases_mis', name: 'Open Cases MIS', description: 'Daily open cases report including drafts', frequency: 'Daily' },
  { id: 'red_alert', name: 'RED Alert', description: 'Discrepancy found during verification', frequency: 'On discrepancy' },
  { id: 'case_completion_report', name: 'Case Completion', description: 'Final report when verification is complete', frequency: 'On completion' },
];

export default function ClientsTab() {
  const [clients, setClients] = useState([]);
  const [entities, setEntities] = useState([]);
  const [packages, setPackages] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddClient, setShowAddClient] = useState(false);
  const [showAddEntity, setShowAddEntity] = useState(false);
  const [showEditClient, setShowEditClient] = useState(null);
  const [showManageUsers, setShowManageUsers] = useState(null);
  const [showAddUser, setShowAddUser] = useState(false);
  const [showEditClientUser, setShowEditClientUser] = useState(false);
  const [selectedEntityForUser, setSelectedEntityForUser] = useState(null); // Track entity when adding user from entity table
  const [showEmailTemplates, setShowEmailTemplates] = useState(null);
  const [showAddCategory, setShowAddCategory] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientUsers, setClientUsers] = useState([]);
  const [entityUsers, setEntityUsers] = useState({}); // Map of entity_id -> users
  const [showEditEntityUser, setShowEditEntityUser] = useState(null);
  const [showEditEntity, setShowEditEntity] = useState(null);
  const [editingEntity, setEditingEntity] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [defaultTemplates, setDefaultTemplates] = useState({});
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [clientSearch, setClientSearch] = useState('');
  const [newCategoryName, setNewCategoryName] = useState('');
  const [activeFormTab, setActiveFormTab] = useState('basic');
  
  // Bulk Import state
  const [showBulkImportDialog, setShowBulkImportDialog] = useState(false);
  const [bulkImportFile, setBulkImportFile] = useState(null);
  const [bulkImportData, setBulkImportData] = useState([]);
  const [bulkImportErrors, setBulkImportErrors] = useState([]);
  const [sendClientInvites, setSendClientInvites] = useState(true);

  const initialClientState = {
    company_name: '',
    contact_name: '',
    email: '',
    phone: '',
    address: '',
    logo_url: '',
    category: '',
    // Billing fields
    billing_type: 'standard',
    package_id: '',
    associated_packages: [], // Multiple packages associated with client
    per_employee_rate: 0,
    custom_check_prices: {},
    verification_type: 'both',
    pre_employment_basic_first: false,
    client_approval_required: false,
    allow_edit_without_approval: false,
    agreement_start_date: '',
    agreement_end_date: '',
    agreement_document_url: '',
    email_templates: {},
    // CC Configuration - linked to Admin Mail Templates
    email_cc_settings: {
      candidate_invitation: false,
      candidate_reminder: false,
      data_submission_notification: true,
      insufficiency_raised: true,
      insufficiency_cleared: true,
      consolidated_insuff_mis: true,
      consolidated_open_cases_mis: true,
      red_alert: true,
      case_completion_report: true
    },
    // MIS Settings
    send_mis: false,
    mis_frequency: 'weekly',
    mis_types: [],  // ['insufficiency_mis', 'status_mis']
    // Dashboard Reports Visibility (for client portal)
    dashboard_reports_visibility: {
      case_summary: true,
      verification_status: true,
      tat_report: true,
      insufficiency_report: true,
      completed_cases: true,
      in_progress_cases: true,
      pending_cases: true,
      monthly_trends: true,
      check_type_breakdown: true
    },
    // Initial Entity & HR User options
    create_initial_entity: false,
    initial_entity_name: '',
    initial_entity_package: '',
    initial_hr_name: '',
    initial_hr_email: '',
    initial_hr_password: '',
    send_hr_invite: true,
    force_hr_password_change: true,
    // Portal invitation for the client Contact Person (May 24, 2026).
    // When ON, after the client is created we auto-provision a
    // client_admin user for the contact person and mail their
    // credentials immediately — no separate Edit step required.
    send_contact_invitation: true,
    // Veecheck Jun 2026 — Per-client PDF report template (only used
    // when category === "BGV Agency"). Empty string = use the default.
    report_template_id: ''
  };

  const [newClient, setNewClient] = useState(initialClientState);

  // Veecheck Jun 2026 — list of available PDF report templates (used by
  // the BGV-Agency-only "Report Template" dropdown on the client form).
  const [reportTemplates, setReportTemplates] = useState([]);

  const [newEntity, setNewEntity] = useState({
    client_id: '',
    entity_name: '',
    package_id: '',
    location: '',
    // Optional HR User creation
    create_hr_user: false,
    hr_name: '',
    hr_email: '',
    hr_password: '',
    hr_role: 'client_user',
    send_invite: true,
    force_password_change: true
  });

  const [newUser, setNewUser] = useState({
    name: '',
    email: '',
    phone: '',
    client_role: 'client_user',
    assigned_entities: [],
    password: '',
    send_invite: true,
    force_password_change: true
  });

  // Generate default password from name
  const generateDefaultPassword = (name) => {
    const namePart = name.split(' ')[0].toLowerCase() || 'user';
    const randomNum = Math.floor(1000 + Math.random() * 9000);
    return `${namePart}@${randomNum}`;
  };

  useEffect(() => {
    fetchData();
    fetchDefaultTemplates();
    fetchCategories();
    // Veecheck Jun 2026 — fetch available PDF report templates for the
    // BGV-Agency dropdown on the client form.
    (async () => {
      try {
        const r = await axios.get('/report-templates');
        setReportTemplates(Array.isArray(r.data) ? r.data : []);
      } catch (e) {
        // Silent — feature degrades gracefully (dropdown stays empty).
      }
    })();
  }, []);

  const fetchData = async () => {
    try {
      const [clientsRes, entitiesRes, packagesRes] = await Promise.all([
        axios.get('/clients?include_inactive=true'),
        axios.get('/entities?include_inactive=true'),
        axios.get('/packages')
      ]);
      setClients(clientsRes.data);
      setEntities(entitiesRes.data);
      setPackages(packagesRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get('/client-categories');
      setCategories(response.data);
    } catch (error) {
      console.error('Failed to fetch categories');
      setCategories(['Employment Verification', 'Banking Verification', 'Tenant Verification']);
    }
  };

  // Fetch users for a specific entity
  const fetchEntityUsers = async (entityId) => {
    try {
      const response = await axios.get(`/entities/${entityId}/contacts`);
      setEntityUsers(prev => ({ ...prev, [entityId]: response.data }));
    } catch (error) {
      console.error('Failed to fetch entity users:', error);
    }
  };

  // Fetch users for all entities of a client
  const fetchAllEntityUsers = async (clientId) => {
    const clientEntities = entities.filter(e => e.client_id === clientId);
    for (const entity of clientEntities) {
      await fetchEntityUsers(entity.id);
    }
  };

  // Update entity user
  const handleUpdateEntityUser = async () => {
    if (!editingUser) return;
    try {
      await axios.put(`/users/${editingUser.id}`, {
        name: editingUser.name,
        email: editingUser.email,
        phone: editingUser.phone,
        assigned_entities: editingUser.assigned_entities
      });
      toast.success('User updated successfully');
      setShowEditEntityUser(null);
      setEditingUser(null);
      // Refresh entity users
      if (editingUser.entity_id) {
        fetchEntityUsers(editingUser.entity_id);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    }
  };

  // Update client user (from Manage Users dialog) — role + assigned entities + name/phone
  const handleUpdateClientUser = async () => {
    if (!editingUser || !showManageUsers) return;
    try {
      await axios.put(`/clients/${showManageUsers.id}/users/${editingUser.id}`, {
        name: editingUser.name,
        email: editingUser.email,  // required by ClientUserCreate schema; backend ignores it
        phone: editingUser.phone || '',
        client_role: editingUser.client_role,
        assigned_entities: editingUser.client_role === 'client_admin' ? [] : (editingUser.assigned_entities || []),
      });
      toast.success('User updated successfully');
      setShowEditClientUser(false);
      setEditingUser(null);
      fetchClientUsers(showManageUsers.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    }
  };

  // Deactivate entity user (soft delete - retains data)
  const handleDeactivateEntityUser = async (user, entityId) => {
    if (!window.confirm(`Remove ${user.name} from this entity?\n\nIf they are also assigned to other entities, they will keep their access there. If this is their only entity, their account will be deactivated.`)) {
      return;
    }
    try {
      const res = await axios.delete(`/entities/${entityId}/users/${user.id}`);
      toast.success(res.data?.message || `${user.name} removed from this entity`);
      fetchEntityUsers(entityId);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove user from entity');
    }
  };

  // Delete entity handler
  const handleDeleteEntity = async (entity, clientId) => {
    if (!window.confirm(`Are you sure you want to delete "${entity.entity_name}"? This action will deactivate associated HR users.`)) {
      return;
    }
    try {
      const response = await axios.delete(`/entities/${entity.id}`);
      toast.success(response.data?.message || 'Entity deleted successfully');
      fetchData();
      // Refresh entity users
      if (entityUsers[entity.id]) {
        fetchEntityUsers(entity.id);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete entity');
    }
  };

  // Delete user handler (hard delete option)
  const handleDeleteUser = async (user, entityId) => {
    if (!window.confirm(`Are you sure you want to permanently delete ${user.name}? This will remove their access completely.`)) {
      return;
    }
    try {
      const response = await axios.delete(`/users/${user.id}`);
      toast.success(response.data?.message || 'User deleted successfully');
      fetchEntityUsers(entityId);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  // Toggle client status
  const handleToggleClientStatus = async (client) => {
    try {
      const response = await axios.patch(`/clients/${client.id}/toggle-status`);
      toast.success(response.data?.message);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to toggle client status');
    }
  };

  // Toggle entity status
  const handleToggleEntityStatus = async (entity) => {
    try {
      const response = await axios.patch(`/entities/${entity.id}/toggle-status`);
      toast.success(response.data?.message);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to toggle entity status');
    }
  };

  // Toggle user status
  const handleToggleUserStatus = async (user, entityId) => {
    try {
      const response = await axios.patch(`/users/${user.id}/toggle-status`);
      toast.success(response.data?.message);
      if (entityId) {
        fetchEntityUsers(entityId);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to toggle user status');
    }
  };

  // Reset user password
  const handleResetUserPassword = async (user, entityId) => {
    if (!window.confirm(`Are you sure you want to reset the password for ${user.name}? A new password will be generated and emailed to them.`)) {
      return;
    }
    try {
      const response = await axios.post(`/users/${user.id}/reset-password`);
      toast.success(`Password reset successfully! New password: ${response.data.new_password}`);
      if (entityId) {
        fetchEntityUsers(entityId);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    }
  };

  // Send portal invitation to a user
  const handleSendInvitation = async (userId, userName) => {
    try {
      const resp = await axios.post(`/users/${userId}/send-invitation`);
      toast.success(`Invitation sent to ${userName}. Password: ${resp.data.new_password}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send invitation');
    }
  };

  // Open Edit Entity dialog - also load HR user info for contact fields
  const openEditEntity = (entity) => {
    // Find HR user(s) for this entity from entityUsers map
    const entUsers = entityUsers[entity.id] || [];
    const primaryUser = entUsers[0];
    
    setEditingEntity({
      id: entity.id,
      client_id: entity.client_id,
      entity_name: entity.entity_name || '',
      location: entity.location || '',
      package_id: entity.package_id || '',
      contact_person: entity.contact_person || primaryUser?.name || '',
      contact_email: entity.contact_email || primaryUser?.email || '',
      contact_phone: entity.contact_phone || primaryUser?.phone || '',
      _hr_user_id: primaryUser?.id || null,
      _hr_role: primaryUser?.client_role || 'client_user'
    });
    setShowEditEntity(true);
  };

  // Update Entity handler
  const handleUpdateEntity = async (e) => {
    e.preventDefault();
    if (!editingEntity) return;
    
    try {
      await axios.put(`/entities/${editingEntity.id}`, editingEntity);
      
      // Update HR user role if changed
      if (editingEntity._hr_user_id && editingEntity._hr_role) {
        const entityUsersList = entityUsers[editingEntity.id] || [];
        const hrUser = entityUsersList.find(u => u.id === editingEntity._hr_user_id);
        if (hrUser && hrUser.client_role !== editingEntity._hr_role) {
          await axios.put(`/clients/${editingEntity.client_id}/users/${editingEntity._hr_user_id}`, {
            name: hrUser.name,
            email: hrUser.email,
            client_role: editingEntity._hr_role
          });
        }
      }
      
      toast.success('Entity updated successfully');
      setShowEditEntity(false);
      setEditingEntity(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update entity');
    }
  };

  // Delete client handler
  const handleDeleteClient = async (client) => {
    if (!window.confirm(`Are you sure you want to delete "${client.company_name}"? This will also remove all associated entities and deactivate users.`)) {
      return;
    }
    try {
      const response = await axios.delete(`/clients/${client.id}`);
      toast.success(response.data?.message || 'Client deleted successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete client');
    }
  };

  // Get entity users by entity ID
  const getEntityUsersList = (entityId) => {
    return entityUsers[entityId] || [];
  };

  const handleAddCategory = async () => {
    if (!newCategoryName.trim()) {
      toast.error('Category name is required');
      return;
    }
    try {
      await axios.post('/client-categories', { name: newCategoryName.trim() });
      toast.success('Category added successfully');
      setNewCategoryName('');
      setShowAddCategory(false);
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add category');
    }
  };

  const fetchDefaultTemplates = async () => {
    try {
      const response = await axios.get('/email-template-defaults');
      setDefaultTemplates(response.data);
    } catch (error) {
      console.error('Failed to fetch default templates');
    }
  };

  const fetchClientUsers = async (clientId) => {
    try {
      const response = await axios.get(`/clients/${clientId}/users`);
      setClientUsers(response.data);
    } catch (error) {
      setClientUsers([]);
    }
  };

  const handleAddClient = async (e) => {
    e.preventDefault();
    if (newClient.phone && newClient.phone.length !== 10) {
      toast.error('Phone number must be exactly 10 digits');
      return;
    }
    try {
      // Create client
      const clientResponse = await axios.post('/clients', newClient);
      const createdClient = clientResponse.data;

      // Auto-provision a Client Admin user for the Contact Person and
      // mail their portal credentials (May 24, 2026). When the toggle
      // is off (or contact email missing) we skip silently — the admin
      // can still trigger this from Edit Client later.
      let contactInviteSent = false;
      let contactInvitePassword = null;
      if (newClient.send_contact_invitation !== false && newClient.email && newClient.contact_name) {
        try {
          const contactPwd = generateDefaultPassword(newClient.contact_name || 'admin');
          await axios.post(`/clients/${createdClient.id}/users`, {
            name: newClient.contact_name,
            email: newClient.email,
            password: contactPwd,
            client_id: createdClient.id,
            client_role: 'client_admin',
            assigned_entities: [],
            send_invite: true,
            force_password_change: true
          });
          contactInviteSent = true;
          contactInvitePassword = contactPwd;
        } catch (inviteErr) {
          // Surface but don't block — client is already created.
          toast.warning('Client created but Contact Person invitation failed: ' + (inviteErr.response?.data?.detail || 'Unknown error'));
        }
      }
      
      // Create initial entity + HR user if checkbox is checked
      if (newClient.create_initial_entity && newClient.initial_entity_name && newClient.initial_entity_package) {
        try {
          // Create entity
          const entityResponse = await axios.post('/entities', {
            client_id: createdClient.id,
            entity_name: newClient.initial_entity_name,
            package_id: newClient.initial_entity_package,
            location: newClient.address || ''
          });
          const createdEntity = entityResponse.data;
          
          // Create HR user if email provided
          if (newClient.initial_hr_email) {
            const hrPassword = newClient.initial_hr_password || generateDefaultPassword(newClient.initial_hr_name || 'hr');
            const userResponse = await axios.post(`/clients/${createdClient.id}/users`, {
              name: newClient.initial_hr_name || 'HR Admin',
              email: newClient.initial_hr_email,
              password: hrPassword,
              client_id: createdClient.id,
              client_role: 'client_admin',
              assigned_entities: [createdEntity.id || createdEntity.entity_id],
              send_invite: newClient.send_hr_invite,
              force_password_change: newClient.force_hr_password_change
            });
            
            toast.success(
              <div>
                <p>Client, Entity & HR User created!</p>
                <p className="text-xs mt-1">HR Login: <strong>{newClient.initial_hr_email}</strong></p>
                <p className="text-xs">Password: <strong>{hrPassword}</strong></p>
                {newClient.send_hr_invite && <p className="text-xs text-green-600">✓ Invitation email sent</p>}
                {newClient.force_hr_password_change && <p className="text-xs text-amber-600">⚠ Password change required on first login</p>}
              </div>,
              { duration: 10000 }
            );
          } else {
            toast.success('Client & Entity created successfully');
          }
        } catch (entityError) {
          toast.warning('Client created but Entity/HR creation failed: ' + (entityError.response?.data?.detail || 'Unknown error'));
        }
      } else {
        if (contactInviteSent) {
          toast.success(
            <div>
              <p>Client added successfully</p>
              <p className="text-xs mt-1">Contact Admin Login: <strong>{newClient.email}</strong></p>
              <p className="text-xs">Password: <strong>{contactInvitePassword}</strong></p>
              <p className="text-xs text-green-600">✓ Invitation email sent</p>
            </div>,
            { duration: 10000 }
          );
        } else {
          toast.success('Client added successfully');
        }
      }
      
      setShowAddClient(false);
      setNewClient(initialClientState);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add client');
    }
  };

  const handleUpdateClient = async (e) => {
    e.preventDefault();
    if (newClient.phone && newClient.phone.length !== 10) {
      toast.error('Phone number must be exactly 10 digits');
      return;
    }
    try {
      await axios.put(`/clients/${showEditClient.id}`, newClient);
      toast.success('Client updated successfully');
      setShowEditClient(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update client');
    }
  };

  // Bulk Import Functions
  const downloadClientTemplate = () => {
    const headers = 'company_name,contact_name,email,phone,address,category,entity_name,entity_location,hr_name,hr_email,package_id,billing_type,per_employee_rate';
    const sampleRow = 'ABC Corp,John Doe,contact@abccorp.com,9876543210,123 Business Park Mumbai,Employment Verification,ABC Corp - HQ,Mumbai,Jane Smith,hr@abccorp.com,,standard,500';
    const csv = `${headers}\n${sampleRow}`;
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'client_import_template.csv';
    a.click();
  };

  // Proper CSV parser that handles quoted fields with newlines
  const parseCSVLine = (text) => {
    const rows = [];
    let currentRow = [];
    let currentField = '';
    let insideQuotes = false;
    
    for (let i = 0; i < text.length; i++) {
      const char = text[i];
      const nextChar = text[i + 1];
      
      if (char === '"') {
        if (insideQuotes && nextChar === '"') {
          // Escaped quote
          currentField += '"';
          i++;
        } else {
          // Toggle quote mode
          insideQuotes = !insideQuotes;
        }
      } else if (char === ',' && !insideQuotes) {
        // End of field
        currentRow.push(currentField.trim());
        currentField = '';
      } else if ((char === '\n' || (char === '\r' && nextChar === '\n')) && !insideQuotes) {
        // End of row
        currentRow.push(currentField.trim());
        if (currentRow.some(f => f)) { // Only add non-empty rows
          rows.push(currentRow);
        }
        currentRow = [];
        currentField = '';
        if (char === '\r') i++; // Skip \n after \r
      } else if (char === '\r' && !insideQuotes) {
        // End of row (old Mac format)
        currentRow.push(currentField.trim());
        if (currentRow.some(f => f)) {
          rows.push(currentRow);
        }
        currentRow = [];
        currentField = '';
      } else {
        currentField += char;
      }
    }
    
    // Don't forget the last field/row
    if (currentField || currentRow.length > 0) {
      currentRow.push(currentField.trim());
      if (currentRow.some(f => f)) {
        rows.push(currentRow);
      }
    }
    
    return rows;
  };

  const parseClientCSV = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result;
      const rows = parseCSVLine(text);
      
      if (rows.length < 2) {
        toast.error('CSV file must have a header row and at least one data row');
        return;
      }
      
      const headers = rows[0].map(h => h.toLowerCase().replace(/\s+/g, '_'));
      
      const data = [];
      const errors = [];
      
      for (let i = 1; i < rows.length; i++) {
        const values = rows[i];
        const row = {};
        
        headers.forEach((header, idx) => {
          row[header] = values[idx] || '';
        });
        
        // Validate required fields
        row.valid = !!(row.company_name && row.email);
        row.errors = [];
        if (!row.company_name) row.errors.push('Company name required');
        if (!row.email) row.errors.push('Email required');
        if (row.email && !row.email.includes('@')) row.errors.push('Invalid email format');
        
        data.push(row);
        if (row.errors.length > 0) {
          errors.push({ row: i, errors: row.errors });
        }
      }
      
      setBulkImportData(data);
      setBulkImportErrors(errors);
      
      if (data.length > 0) {
        toast.success(`Parsed ${data.length} rows. ${data.filter(d => d.valid).length} valid for import.`);
      }
    };
    reader.readAsText(file);
  };

  const handleBulkImportClients = async () => {
    const validRecords = bulkImportData.filter(d => d.valid);
    if (validRecords.length === 0) {
      toast.error('No valid records to import');
      return;
    }

    try {
      const response = await axios.post('/clients/bulk-import', {
        send_invites: sendClientInvites,
        clients: validRecords.map(r => ({
          company_name: r.company_name,
          contact_name: r.contact_name || r.company_name,
          email: r.email,
          phone: r.phone || '',
          address: r.address || '',
          category: r.category || 'Employment Verification',
          entity_name: r.entity_name || '',
          entity_location: r.entity_location || r.address || '',
          hr_name: r.hr_name || r.contact_name || '',
          hr_email: r.hr_email || '',
          package_id: r.package_id || '',
          billing_type: r.billing_type || 'standard',
          per_employee_rate: parseFloat(r.per_employee_rate) || 0
        }))
      });
      
      toast.success(
        <div>
          <p>Successfully imported {response.data.imported} clients</p>
          {sendClientInvites && response.data.imported > 0 && (
            <p className="text-xs mt-1">Invitation emails sent to HR contacts</p>
          )}
          {response.data.skipped > 0 && (
            <p className="text-xs text-amber-600">{response.data.skipped} skipped due to errors</p>
          )}
        </div>,
        { duration: 8000 }
      );
      
      setShowBulkImportDialog(false);
      setBulkImportFile(null);
      setBulkImportData([]);
      setBulkImportErrors([]);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Bulk import failed');
    }
  };

  // Export Clients Function
  const handleExportClients = async () => {
    try {
      toast.info('Preparing export...');
      const response = await axios.get('/clients/export/csv');
      const data = response.data.data;
      
      if (data.length === 0) {
        toast.warning('No clients to export');
        return;
      }
      
      // Build CSV content
      const headers = Object.keys(data[0]);
      const csvContent = [
        headers.join(','),
        ...data.map(row => headers.map(h => {
          const val = row[h] || '';
          // Escape quotes and wrap in quotes if contains comma
          const escaped = String(val).replace(/"/g, '""');
          return escaped.includes(',') || escaped.includes('\n') ? `"${escaped}"` : escaped;
        }).join(','))
      ].join('\n');
      
      // Download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `clients_export_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      
      toast.success(`Exported ${data.length} clients`);
    } catch (error) {
      toast.error('Failed to export clients');
      console.error(error);
    }
  };

  const handleAddEntity = async (e) => {
    e.preventDefault();
    try {
      // Create entity
      const entityResponse = await axios.post('/entities', {
        client_id: newEntity.client_id,
        entity_name: newEntity.entity_name,
        package_id: newEntity.package_id,
        location: newEntity.location
      });
      
      const createdEntity = entityResponse.data;
      
      // Create HR user if checkbox is checked
      if (newEntity.create_hr_user && newEntity.hr_email) {
        try {
          const client = clients.find(c => c.id === newEntity.client_id);
          await axios.post(`/clients/${newEntity.client_id}/users`, {
            name: newEntity.hr_name,
            email: newEntity.hr_email,
            password: newEntity.hr_password || 'hr123',
            client_id: newEntity.client_id,
            client_role: newEntity.hr_role || 'client_user',
            assigned_entities: [createdEntity.id || createdEntity.entity_id]
          });
          
          const password = newEntity.hr_password || 'hr123';
          toast.success(
            <div>
              <p>Entity & HR User created successfully!</p>
              <p className="text-xs mt-1">HR Login: <strong>{newEntity.hr_email}</strong></p>
              <p className="text-xs">Password: <strong>{password}</strong></p>
            </div>,
            { duration: 10000 }
          );
        } catch (userError) {
          toast.warning('Entity created but HR user creation failed: ' + (userError.response?.data?.detail || 'Unknown error'));
        }
      } else {
        toast.success('Entity added successfully');
      }
      
      setShowAddEntity(false);
      setNewEntity({ client_id: '', entity_name: '', package_id: '', location: '', create_hr_user: false, hr_name: '', hr_email: '', hr_password: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add entity');
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    // Determine which client we're adding for - could be from showManageUsers or selectedClient
    const targetClient = showManageUsers || selectedClient;
    if (!targetClient) {
      toast.error('No client selected');
      return;
    }

    // If adding for a specific entity, automatically assign that entity
    const assignedEntities = selectedEntityForUser
      ? [selectedEntityForUser.id]
      : newUser.assigned_entities;

    const submitUser = async (forceReassign) => {
      return axios.post(`/clients/${targetClient.id}/users`, {
        ...newUser,
        password: newUser.password || 'hr123',
        client_id: targetClient.id,
        assigned_entities: assignedEntities,
        force_reassign: forceReassign,
      });
    };

    const finishAddUser = (responseData) => {
      toast.success(responseData?.message || 'User added successfully');
      setShowAddUser(false);
      setSelectedEntityForUser(null);
      setNewUser({ name: '', email: '', phone: '', client_role: 'client_user', assigned_entities: [], password: '' });
      if (showManageUsers) {
        fetchClientUsers(showManageUsers.id);
      }
      if (selectedEntityForUser) {
        fetchEntityUsers(selectedEntityForUser.id);
      }
    };

    try {
      const res = await submitUser(false);
      finishAddUser(res.data);
    } catch (error) {
      // 409 = cross-client collision. The backend returns a structured detail
      // dict explaining which client owns the email. Show the admin the
      // context and let them explicitly force-reassign.
      const status = error.response?.status;
      const detail = error.response?.data?.detail;
      const isStructuredCollision = status === 409 && detail && typeof detail === 'object'
        && detail.code === 'user_belongs_to_other_client';
      if (isStructuredCollision) {
        const otherClient = detail.existing_client_name || '(unknown client)';
        const otherEntities = detail.existing_assigned_entities_count ?? 0;
        const otherActive = detail.existing_is_active ? 'active' : 'inactive';
        const confirmMsg = (
          `${detail.message}\n\n` +
          `Existing record:\n` +
          `  • Name: ${detail.existing_name || '(none)'}\n` +
          `  • Belongs to: ${otherClient}\n` +
          `  • Currently on ${otherEntities} entit${otherEntities === 1 ? 'y' : 'ies'} (${otherActive})\n\n` +
          `Force-reassign to ${targetClient.company_name || 'this client'}? ` +
          `Their previous entity assignments will be REPLACED with the new ones.`
        );
        if (window.confirm(confirmMsg)) {
          try {
            const res2 = await submitUser(true);
            finishAddUser(res2.data);
          } catch (err2) {
            toast.error(err2.response?.data?.detail?.message || err2.response?.data?.detail || 'Force-reassign failed');
          }
        }
      } else {
        const message = typeof detail === 'string' ? detail : (detail?.message || 'Failed to add user');
        toast.error(message);
      }
    }
  };

  const openEditClient = (client) => {
    setNewClient({
      company_name: client.company_name,
      contact_name: client.contact_name,
      email: client.email,
      phone: client.phone,
      address: client.address || '',
      logo_url: client.logo_url || '',
      category: client.category || 'Employment Verification',
      billing_type: client.billing_type || 'standard',
      package_id: client.package_id || '',
      associated_packages: client.associated_packages || [],
      per_employee_rate: client.per_employee_rate || 0,
      custom_check_prices: client.custom_check_prices || {},
      verification_type: client.verification_type || 'both',
      pre_employment_basic_first: client.pre_employment_basic_first || false,
      client_approval_required: client.client_approval_required || false,
      allow_edit_without_approval: client.allow_edit_without_approval || false,
      agreement_start_date: client.agreement_start_date || '',
      agreement_end_date: client.agreement_end_date || '',
      agreement_document_url: client.agreement_document_url || '',
      email_templates: client.email_templates || {},
      // CC Settings for email templates
      email_cc_settings: client.email_cc_settings || {
        invitation: false,
        reminder: false,
        case_completed: true,
        mis_report: true,
        progress_update: false,
        red_alert: true,
        insufficiency: false
      },
      // Dashboard Reports Visibility
      dashboard_reports_visibility: client.dashboard_reports_visibility || {
        case_summary: true,
        verification_status: true,
        tat_report: true,
        insufficiency_report: true,
        completed_cases: true,
        in_progress_cases: true,
        pending_cases: true,
        monthly_trends: true,
        check_type_breakdown: true
      },
      // MIS Settings
      send_mis: client.send_mis || false,
      mis_frequency: client.mis_frequency || 'weekly',
      mis_types: client.mis_types || [],
      // Veecheck Jun 2026 — per-client report template (BGV Agency only)
      report_template_id: client.report_template_id || ''
    });
    setActiveFormTab('basic'); // Reset to first tab when opening
    setShowEditClient(client);
    // Fetch client users for the invitation button
    fetchClientUsers(client.id);
  };

  const openManageUsers = (client) => {
    setShowManageUsers(client);
    fetchClientUsers(client.id);
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setNewClient({ ...newClient, logo_url: reader.result });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleAgreementUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setNewClient({ ...newClient, agreement_document_url: reader.result });
      };
      reader.readAsDataURL(file);
    }
  };

  const saveEmailTemplate = async (templateId, content) => {
    const updatedTemplates = { ...newClient.email_templates, [templateId]: content };
    setNewClient({ ...newClient, email_templates: updatedTemplates });
    
    if (showEmailTemplates) {
      try {
        await axios.put(`/clients/${showEmailTemplates.id}`, { email_templates: updatedTemplates });
        toast.success('Template saved');
        fetchData();
      } catch (error) {
        toast.error('Failed to save template');
      }
    }
    setEditingTemplate(null);
  };

  const getClientEntities = (clientId) => entities.filter(e => e.client_id === clientId);
  const getPackageName = (packageId) => packages.find(p => p.id === packageId)?.name || 'N/A';
  const getEntityName = (entityId) => entities.find(e => e.id === entityId)?.entity_name || 'N/A';

  // Filter clients by category and search
  const filteredClients = clients.filter(c => {
    const matchesCategory = categoryFilter === 'all' || c.category === categoryFilter;
    const search = clientSearch.toLowerCase().trim();
    const matchesSearch = !search || 
      (c.company_name || '').toLowerCase().includes(search) ||
      (c.contact_name || '').toLowerCase().includes(search) ||
      (c.email || '').toLowerCase().includes(search) ||
      (c.phone || '').includes(search) ||
      (c.client_code || '').toLowerCase().includes(search);
    return matchesCategory && matchesSearch;
  });

  // Get category badge color
  const getCategoryColor = (category) => {
    const colors = {
      'Employment Verification': 'bg-blue-100 text-blue-700 border-blue-200',
      'Banking Verification': 'bg-emerald-100 text-emerald-700 border-emerald-200',
      'Tenant Verification': 'bg-purple-100 text-purple-700 border-purple-200'
    };
    return colors[category] || 'bg-slate-100 text-slate-700 border-slate-200';
  };

  if (loading) return <div className="p-8 text-center">Loading...</div>;

  // Render form fields directly (not as a component to prevent focus loss)
  const renderClientFormFields = (isEdit = false) => (
    <Tabs value={activeFormTab} onValueChange={setActiveFormTab} className="w-full">
      <TabsList className="grid w-full grid-cols-5">
        <TabsTrigger value="basic">Basic Info</TabsTrigger>
        <TabsTrigger value="billing">Billing</TabsTrigger>
        <TabsTrigger value="verification">Verification</TabsTrigger>
        <TabsTrigger value="agreement">Agreement</TabsTrigger>
        <TabsTrigger value="settings">Settings</TabsTrigger>
      </TabsList>

      <TabsContent value="basic" className="space-y-4 mt-4">
        {/* Logo Upload */}
        <div>
          <Label>Company Logo</Label>
          <div className="mt-2 flex items-center gap-4">
            <div className="w-20 h-20 rounded-lg bg-slate-100 flex items-center justify-center overflow-hidden border-2 border-dashed border-slate-300">
              {newClient.logo_url ? (
                <img src={newClient.logo_url} alt="Logo" className="w-full h-full object-cover" />
              ) : (
                <Image className="w-8 h-8 text-slate-400" />
              )}
            </div>
            <div>
              <Input type="file" accept="image/*" onChange={handleLogoUpload} className="max-w-xs" />
              <p className="text-xs text-slate-500 mt-1">PNG, JPG up to 2MB</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Company Name *</Label>
            <Input value={newClient.company_name} onChange={(e) => setNewClient({ ...newClient, company_name: e.target.value })} required />
          </div>
          <div>
            <Label>Category *</Label>
            <div className="flex gap-2">
              <Select 
                value={newClient.category} 
                onValueChange={(v) => {
                  if (v === 'add_new') {
                    setShowAddCategory(true);
                  } else {
                    setNewClient({ ...newClient, category: v });
                  }
                }}
              >
                <SelectTrigger className="flex-1">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((cat) => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                  <SelectItem value="add_new" className="text-blue-600 font-medium">
                    <span className="flex items-center gap-1">
                      <Plus className="w-3 h-3" /> Add New Category
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            {/* Veecheck Jun 2026 — Report Template selector. Only shown
                when the category is "BGV Agency" (other categories use
                the system default automatically). */}
            {newClient.category === 'BGV Agency' && (
              <div className="mt-2">
                <Label className="text-xs text-slate-600">PDF Report Template</Label>
                <Select
                  value={newClient.report_template_id || '__default__'}
                  onValueChange={(v) => setNewClient({ ...newClient, report_template_id: v === '__default__' ? '' : v })}
                >
                  <SelectTrigger data-testid="report-template-select">
                    <SelectValue placeholder="Use default" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__default__">Use default (Veecheck Address)</SelectItem>
                    {reportTemplates.map((t) => (
                      <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-[10px] text-slate-500 mt-1">
                  Choose which PDF report template is emailed to this client on case completion.
                </p>
              </div>
            )}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Contact Name *</Label>
            <Input value={newClient.contact_name} onChange={(e) => setNewClient({ ...newClient, contact_name: e.target.value })} required />
          </div>
          <div>
            <Label>Email *</Label>
            <Input type="email" value={newClient.email} onChange={(e) => setNewClient({ ...newClient, email: e.target.value })} required />
          </div>
        </div>
        {isEdit && (
          <div>
            <Label>Client Code (for API integration)</Label>
            <Input
              value={newClient.client_code || ''}
              onChange={(e) => setNewClient({ ...newClient, client_code: e.target.value.toUpperCase() })}
              placeholder="e.g., RTX"
              className="font-mono max-w-xs"
            />
            <p className="text-xs text-slate-400 mt-1">Short code used in external API calls instead of UUID</p>
          </div>
        )}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Phone *</Label>
            <Input 
              type="tel" 
              maxLength={10} 
              pattern="[0-9]{10}" 
              value={newClient.phone} 
              onChange={(e) => {
                const val = e.target.value.replace(/\D/g, '').slice(0, 10);
                setNewClient({ ...newClient, phone: val });
              }} 
              placeholder="10-digit phone number"
              required 
            />
            {newClient.phone && newClient.phone.length > 0 && newClient.phone.length < 10 && (
              <p className="text-xs text-amber-600 mt-1">{10 - newClient.phone.length} more digits needed</p>
            )}
          </div>
          <div>
            <Label>Address</Label>
            <Input value={newClient.address} onChange={(e) => setNewClient({ ...newClient, address: e.target.value })} />
          </div>
        </div>
        
        {/* Portal Invitation for Client Contact Person — May 24, 2026 */}
        {!isEdit && (
          <div className="border rounded-lg p-4 bg-emerald-50 mt-4" data-testid="client-contact-invite-section">
            <div className="flex items-start gap-2">
              <input
                type="checkbox"
                id="send_contact_invitation"
                checked={newClient.send_contact_invitation !== false}
                onChange={(e) => setNewClient({ ...newClient, send_contact_invitation: e.target.checked })}
                className="rounded border-slate-300 mt-1"
                data-testid="send-contact-invitation-toggle"
              />
              <label htmlFor="send_contact_invitation" className="text-sm cursor-pointer">
                <span className="font-medium text-slate-700">Send portal invitation to Contact Person</span>
                <p className="text-xs text-slate-500 mt-0.5">
                  Creates a Client Admin login for <strong>{newClient.contact_name || 'the contact person'}</strong>
                  {newClient.email ? <> (<strong>{newClient.email}</strong>)</> : null} and emails the credentials immediately.
                </p>
              </label>
            </div>
          </div>
        )}

        {/* Initial Entity + HR User (Only for new client) */}
        {!isEdit && (
          <div className="border rounded-lg p-4 bg-blue-50 mt-4">
            <div className="flex items-center gap-2 mb-3">
              <input
                type="checkbox"
                id="create_initial_entity"
                checked={newClient.create_initial_entity}
                onChange={(e) => setNewClient({ ...newClient, create_initial_entity: e.target.checked })}
                className="rounded border-slate-300"
              />
              <label htmlFor="create_initial_entity" className="text-sm font-medium cursor-pointer">Create Initial Entity & HR User</label>
            </div>
            
            {newClient.create_initial_entity && (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Entity Name *</Label>
                    <Input 
                      value={newClient.initial_entity_name || ''} 
                      onChange={(e) => setNewClient({ ...newClient, initial_entity_name: e.target.value })} 
                      placeholder="e.g., Head Office"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Package *</Label>
                    <select 
                      value={newClient.initial_entity_package || ''} 
                      onChange={(e) => setNewClient({ ...newClient, initial_entity_package: e.target.value })} 
                      className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm"
                    >
                      <option value="">Select Package</option>
                      {packages.map(pkg => <option key={pkg.id} value={pkg.id}>{pkg.name}</option>)}
                    </select>
                  </div>
                </div>
                
                <div className="border-t pt-3 mt-3">
                  <p className="text-xs font-medium text-slate-600 mb-2">HR Admin Credentials</p>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <Label className="text-xs">HR Name *</Label>
                      <Input 
                        value={newClient.initial_hr_name || ''} 
                        onChange={(e) => setNewClient({ ...newClient, initial_hr_name: e.target.value })} 
                        placeholder="HR Admin"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">HR Email *</Label>
                      <Input 
                        type="email"
                        value={newClient.initial_hr_email || ''} 
                        onChange={(e) => setNewClient({ ...newClient, initial_hr_email: e.target.value })} 
                        placeholder="hr@company.com"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Password</Label>
                      <div className="flex gap-2">
                        <Input 
                          type="text"
                          value={newClient.initial_hr_password || ''} 
                          onChange={(e) => setNewClient({ ...newClient, initial_hr_password: e.target.value })} 
                          placeholder="Auto-generated if blank"
                          className="flex-1"
                        />
                        <Button 
                          type="button" 
                          variant="outline" 
                          size="sm"
                          onClick={() => setNewClient({ ...newClient, initial_hr_password: generateDefaultPassword(newClient.initial_hr_name || 'hr') })}
                        >
                          Generate
                        </Button>
                      </div>
                    </div>
                  </div>
                  
                  {/* Invite & Password Options */}
                  <div className="flex items-center gap-6 mt-3 p-3 bg-blue-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <Checkbox 
                        id="send-hr-invite"
                        checked={newClient.send_hr_invite}
                        onCheckedChange={(v) => setNewClient({ ...newClient, send_hr_invite: v })}
                      />
                      <Label htmlFor="send-hr-invite" className="text-sm cursor-pointer flex items-center gap-1">
                        <Mail className="w-3 h-3" />
                        Send invitation email
                      </Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <Checkbox 
                        id="force-hr-password"
                        checked={newClient.force_hr_password_change}
                        onCheckedChange={(v) => setNewClient({ ...newClient, force_hr_password_change: v })}
                      />
                      <Label htmlFor="force-hr-password" className="text-sm cursor-pointer flex items-center gap-1">
                        <Shield className="w-3 h-3" />
                        Force password change on first login
                      </Label>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </TabsContent>

      <TabsContent value="billing" className="space-y-4 mt-4">
        <div>
          <Label>Billing Type *</Label>
          <Select value={newClient.billing_type || 'standard'} onValueChange={(v) => setNewClient({ ...newClient, billing_type: v })}>
            <SelectTrigger>
              <SelectValue placeholder="Select billing type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="standard">Standard Package - Per Employee Rate</SelectItem>
              <SelectItem value="custom">Custom Package - Per Check Rate</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-xs text-slate-500 mt-1">
            {newClient.billing_type === 'standard' 
              ? 'Client will be billed a fixed rate per employee for completed verifications'
              : 'Client will be billed based on individual check prices'}
          </p>
        </div>

        {newClient.billing_type === 'standard' && (
          <>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Linked Package (Optional)</Label>
                <Select value={newClient.package_id || ''} onValueChange={(v) => setNewClient({ ...newClient, package_id: v })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select package" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No package</SelectItem>
                    {packages.map(pkg => (
                      <SelectItem key={pkg.id} value={pkg.id}>{pkg.name} - ₹{pkg.price}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Per Employee Rate (₹) *</Label>
                <Input 
                  type="number" 
                  value={newClient.per_employee_rate || ''} 
                  onChange={(e) => setNewClient({ ...newClient, per_employee_rate: parseFloat(e.target.value) || 0 })}
                  placeholder="Enter rate per employee"
                />
              </div>
            </div>
            
            {/* Associated Packages for HR Selection */}
            <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <Label className="text-blue-800">Associated Packages (for HR Selection)</Label>
              <p className="text-xs text-blue-600 mb-3">Select packages that HR can choose from when adding employees</p>
              <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto">
                {packages.map(pkg => (
                  <label key={pkg.id} className="flex items-center gap-2 text-sm cursor-pointer py-1 px-2 rounded hover:bg-blue-100">
                    <input
                      type="checkbox"
                      checked={(newClient.associated_packages || []).includes(pkg.id)}
                      onChange={(e) => {
                        const currentPkgs = newClient.associated_packages || [];
                        if (e.target.checked) {
                          setNewClient({...newClient, associated_packages: [...currentPkgs, pkg.id]});
                        } else {
                          setNewClient({...newClient, associated_packages: currentPkgs.filter(id => id !== pkg.id)});
                        }
                      }}
                      className="rounded border-blue-300"
                    />
                    <span className="font-medium">{pkg.name}</span>
                    <span className="text-xs text-blue-500">₹{pkg.price}</span>
                  </label>
                ))}
              </div>
              {(newClient.associated_packages || []).length > 0 && (
                <p className="text-xs text-blue-700 mt-2">
                  {(newClient.associated_packages || []).length} package(s) selected for HR
                </p>
              )}
            </div>
          </>
        )}

        {newClient.billing_type === 'custom' && (
          <div className="space-y-4">
            <Label>Custom Check Prices</Label>
            <p className="text-xs text-slate-500">Set individual prices for each check type</p>
            <div className="grid grid-cols-2 gap-3">
              {[
                { id: 'id_check', name: 'ID Proof Check' },
                { id: 'address_check', name: 'Address Check' },
                { id: 'criminal_court_check', name: 'Criminal Court Check' },
                { id: 'criminal_advocate_check', name: 'Criminal Advocate Check' },
                { id: 'employment_check', name: 'Employment Check' },
                { id: 'education_check', name: 'Education Check' },
                { id: 'credit_check', name: 'Credit Check' },
                { id: 'drug_test', name: 'Drug Test' },
                { id: 'reference_check', name: 'Reference Check' },
              ].map(check => (
                <div key={check.id} className="flex items-center gap-2">
                  <Label className="w-40 text-sm">{check.name}</Label>
                  <Input
                    type="number"
                    className="w-24"
                    placeholder="₹"
                    value={newClient.custom_check_prices?.[check.id] || ''}
                    onChange={(e) => setNewClient({
                      ...newClient,
                      custom_check_prices: {
                        ...newClient.custom_check_prices,
                        [check.id]: parseFloat(e.target.value) || 0
                      }
                    })}
                  />
                </div>
              ))}
            </div>
          </div>
        )}
      </TabsContent>

      <TabsContent value="verification" className="space-y-4 mt-4">
        <div>
          <Label>Verification Type *</Label>
          <Select value={newClient.verification_type} onValueChange={(v) => setNewClient({ ...newClient, verification_type: v })}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="pre_employment">Pre-Employment Only</SelectItem>
              <SelectItem value="post_employment">Post-Employment Only</SelectItem>
              <SelectItem value="both">Both Pre & Post Employment</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {(newClient.verification_type === 'pre_employment' || newClient.verification_type === 'both') && (
          <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg space-y-4">
            <h4 className="font-medium text-amber-800">Pre-Employment Settings</h4>
            
            <div className="flex items-center justify-between">
              <div>
                <Label className="font-medium">Process Basic Verification First</Label>
                <p className="text-xs text-slate-500">Lock basic details after initial submission for early verification</p>
              </div>
              <Switch
                checked={newClient.pre_employment_basic_first}
                onCheckedChange={(v) => setNewClient({ ...newClient, pre_employment_basic_first: v })}
              />
            </div>
          </div>
        )}

        <div className="space-y-4 p-4 bg-slate-50 rounded-lg">
          <h4 className="font-medium">Workflow Settings</h4>
          
          <div className="flex items-center justify-between">
            <div>
              <Label className="font-medium">Client Approval Required</Label>
              <p className="text-xs text-slate-500">Require client approval before QC can process submitted data</p>
            </div>
            <Switch
              checked={newClient.client_approval_required}
              onCheckedChange={(v) => setNewClient({ ...newClient, client_approval_required: v })}
            />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <Label className="font-medium">Allow Edit Without Approval</Label>
              <p className="text-xs text-slate-500">Allow editing basic details without QC approval</p>
            </div>
            <Switch
              checked={newClient.allow_edit_without_approval}
              onCheckedChange={(v) => setNewClient({ ...newClient, allow_edit_without_approval: v })}
            />
          </div>
        </div>

        {/* MIS Report Settings */}
        <div className="space-y-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
          <div className="flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-blue-600" />
            <h4 className="font-medium text-blue-900">MIS Report Settings</h4>
          </div>
          
          <div className="flex items-center justify-between">
            <div>
              <Label className="font-medium">Send MIS Reports</Label>
              <p className="text-xs text-slate-500">Automatically send Management Information System reports to client</p>
            </div>
            <Switch
              checked={newClient.send_mis}
              onCheckedChange={(v) => setNewClient({ ...newClient, send_mis: v })}
              data-testid="send-mis-toggle"
            />
          </div>

          {newClient.send_mis && (
            <div className="space-y-4 pt-4 border-t border-blue-200">
              <div>
                <Label className="font-medium">Frequency</Label>
                <Select 
                  value={newClient.mis_frequency} 
                  onValueChange={(v) => setNewClient({ ...newClient, mis_frequency: v })}
                >
                  <SelectTrigger className="mt-1" data-testid="mis-frequency-select">
                    <SelectValue placeholder="Select frequency" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="daily">Daily</SelectItem>
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="font-medium">MIS Report Types</Label>
                <p className="text-xs text-slate-500 mb-2">Select which reports to include</p>
                <div className="space-y-3">
                  <div className="flex items-center space-x-3 p-3 bg-white rounded-lg border border-blue-100">
                    <Checkbox
                      id="insuff_mis"
                      checked={newClient.mis_types?.includes('insufficiency_mis')}
                      onCheckedChange={(checked) => {
                        const types = newClient.mis_types || [];
                        if (checked) {
                          setNewClient({ ...newClient, mis_types: [...types, 'insufficiency_mis'] });
                        } else {
                          setNewClient({ ...newClient, mis_types: types.filter(t => t !== 'insufficiency_mis') });
                        }
                      }}
                    />
                    <div>
                      <Label htmlFor="insuff_mis" className="font-medium cursor-pointer">Insufficiency MIS</Label>
                      <p className="text-xs text-slate-500">Report on all insufficiencies raised, status, and TAT</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3 p-3 bg-white rounded-lg border border-blue-100">
                    <Checkbox
                      id="status_mis"
                      checked={newClient.mis_types?.includes('status_mis')}
                      onCheckedChange={(checked) => {
                        const types = newClient.mis_types || [];
                        if (checked) {
                          setNewClient({ ...newClient, mis_types: [...types, 'status_mis'] });
                        } else {
                          setNewClient({ ...newClient, mis_types: types.filter(t => t !== 'status_mis') });
                        }
                      }}
                    />
                    <div>
                      <Label htmlFor="status_mis" className="font-medium cursor-pointer">Status MIS</Label>
                      <p className="text-xs text-slate-500">Report on case status, check progress, and completion rates</p>
                    </div>
                  </div>
                </div>
              </div>

              {newClient.mis_types?.length > 0 && (
                <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                  <p className="text-sm text-emerald-700">
                    <strong>Summary:</strong> {newClient.mis_types.map(t => t === 'insufficiency_mis' ? 'Insufficiency MIS' : 'Status MIS').join(' + ')} 
                    {' '}will be sent <strong>{newClient.mis_frequency}</strong> to {newClient.email || 'client email'}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </TabsContent>

      <TabsContent value="agreement" className="space-y-4 mt-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Agreement Start Date</Label>
            <Input 
              type="date" 
              value={newClient.agreement_start_date} 
              onChange={(e) => setNewClient({ ...newClient, agreement_start_date: e.target.value })} 
            />
          </div>
          <div>
            <Label>Agreement End Date</Label>
            <Input 
              type="date" 
              value={newClient.agreement_end_date} 
              onChange={(e) => setNewClient({ ...newClient, agreement_end_date: e.target.value })} 
            />
          </div>
        </div>

        <div>
          <Label>Agreement Document</Label>
          <div className="mt-2 flex items-center gap-4">
            {newClient.agreement_document_url ? (
              <div className="flex items-center gap-2 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                <FileText className="w-5 h-5 text-emerald-600" />
                <span className="text-sm text-emerald-700">Agreement uploaded</span>
                <Button 
                  type="button" 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => setNewClient({ ...newClient, agreement_document_url: '' })}
                  className="text-rose-600"
                >
                  Remove
                </Button>
              </div>
            ) : (
              <div>
                <Input type="file" accept=".pdf,.doc,.docx" onChange={handleAgreementUpload} />
                <p className="text-xs text-slate-500 mt-1">PDF, DOC, DOCX files</p>
              </div>
            )}
          </div>
        </div>
      </TabsContent>

      <TabsContent value="settings" className="space-y-4 mt-4">
        <div className="p-4 bg-slate-50 rounded-lg">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h4 className="font-medium">Email CC Settings</h4>
              <p className="text-xs text-slate-500">Configure which email types should CC the client. Templates are managed in Admin Settings.</p>
            </div>
          </div>
          
          <p className="text-xs text-slate-600 mb-3 p-2 bg-blue-50 rounded border border-blue-200">
            <strong>Note:</strong> Enable CC to copy client main contact ({newClient.email || 'client email'}) on specific email types. Templates can be customized in Admin Settings → Mail Templates.
          </p>
          
          <div className="space-y-2">
            {EMAIL_TEMPLATE_TYPES.map((template) => (
              <div key={template.id} className="flex items-center justify-between p-3 bg-white rounded border">
                <div className="flex-1">
                  <p className="text-sm font-medium">{template.name}</p>
                  <p className="text-xs text-slate-500">{template.description}</p>
                </div>
                <div className="flex items-center gap-4">
                  <Badge variant="outline" className="text-xs">{template.frequency}</Badge>
                  {/* CC Toggle */}
                  <div className="flex items-center gap-2">
                    <Label className="text-xs text-slate-500 cursor-pointer" htmlFor={`cc-${template.id}`}>CC Client</Label>
                    <Switch
                      id={`cc-${template.id}`}
                      checked={newClient.email_cc_settings?.[template.id] || false}
                      onCheckedChange={(v) => {
                        const ccSettings = { ...(newClient.email_cc_settings || {}) };
                        ccSettings[template.id] = v;
                        setNewClient({ ...newClient, email_cc_settings: ccSettings });
                      }}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Dashboard Reports Visibility for Client Portal */}
        <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
          <div className="flex items-center gap-2 mb-4">
            <Eye className="w-5 h-5 text-purple-600" />
            <div>
              <h4 className="font-medium text-purple-900">Client Dashboard Reports Visibility</h4>
              <p className="text-xs text-purple-600">Control which reports are visible to the client in their dashboard</p>
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-3">
            {[
              { id: 'case_summary', label: 'Case Summary', desc: 'Overview of all cases' },
              { id: 'verification_status', label: 'Verification Status', desc: 'Status of each verification' },
              { id: 'tat_report', label: 'TAT Report', desc: 'Turn-around time analytics' },
              { id: 'insufficiency_report', label: 'Insufficiency Report', desc: 'Pending insufficiencies' },
              { id: 'completed_cases', label: 'Completed Cases', desc: 'All completed verifications' },
              { id: 'in_progress_cases', label: 'In Progress Cases', desc: 'Cases under processing' },
              { id: 'pending_cases', label: 'Pending Cases', desc: 'Cases awaiting action' },
              { id: 'monthly_trends', label: 'Monthly Trends', desc: 'Month-wise trends chart' },
              { id: 'check_type_breakdown', label: 'Check Type Breakdown', desc: 'Distribution by check type' },
            ].map((report) => (
              <div key={report.id} className="flex items-center justify-between p-3 bg-white rounded-lg border border-purple-100">
                <div className="flex items-center gap-2">
                  {newClient.dashboard_reports_visibility?.[report.id] !== false ? (
                    <Eye className="w-4 h-4 text-purple-600" />
                  ) : (
                    <EyeOff className="w-4 h-4 text-slate-400" />
                  )}
                  <div>
                    <Label className="font-medium text-sm cursor-pointer">{report.label}</Label>
                    <p className="text-xs text-slate-500">{report.desc}</p>
                  </div>
                </div>
                <Switch
                  checked={newClient.dashboard_reports_visibility?.[report.id] !== false}
                  onCheckedChange={(v) => {
                    const visibility = { ...(newClient.dashboard_reports_visibility || {}) };
                    visibility[report.id] = v;
                    setNewClient({ ...newClient, dashboard_reports_visibility: visibility });
                  }}
                />
              </div>
            ))}
          </div>
          
          <div className="mt-4 flex gap-2">
            <Button 
              type="button" 
              variant="outline" 
              size="sm"
              className="text-purple-700 border-purple-300"
              onClick={() => {
                const allVisible = {};
                ['case_summary', 'verification_status', 'tat_report', 'insufficiency_report', 'completed_cases', 'in_progress_cases', 'pending_cases', 'monthly_trends', 'check_type_breakdown'].forEach(k => allVisible[k] = true);
                setNewClient({ ...newClient, dashboard_reports_visibility: allVisible });
              }}
            >
              <Eye className="w-4 h-4 mr-1" />
              Show All
            </Button>
            <Button 
              type="button" 
              variant="outline" 
              size="sm"
              className="text-slate-600 border-slate-300"
              onClick={() => {
                const allHidden = {};
                ['case_summary', 'verification_status', 'tat_report', 'insufficiency_report', 'completed_cases', 'in_progress_cases', 'pending_cases', 'monthly_trends', 'check_type_breakdown'].forEach(k => allHidden[k] = false);
                setNewClient({ ...newClient, dashboard_reports_visibility: allHidden });
              }}
            >
              <EyeOff className="w-4 h-4 mr-1" />
              Hide All
            </Button>
          </div>
        </div>
      </TabsContent>
    </Tabs>
  );

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-4xl font-bold text-slate-900 mb-2">Clients</h1>
          <p className="text-slate-600">Manage your client organizations and their settings</p>
        </div>
        <div className="flex gap-2">
          <Button 
            onClick={() => setShowBulkImportDialog(true)}
            variant="outline"
            className="border-slate-600 text-slate-600 hover:bg-slate-50"
            data-testid="import-clients-button"
          >
            <Upload className="w-4 h-4 mr-2" />
            Import Clients
          </Button>
          <Button 
            onClick={handleExportClients}
            variant="outline"
            className="border-emerald-600 text-emerald-600 hover:bg-emerald-50"
            data-testid="export-clients-button"
          >
            <Download className="w-4 h-4 mr-2" />
            Export Clients
          </Button>
          <Button onClick={() => { setNewClient(initialClientState); setActiveFormTab('basic'); setShowAddClient(true); }} className="bg-slate-900 hover:bg-slate-800" data-testid="add-client-button">
            <Plus className="w-4 h-4 mr-2" />
            Add Client
          </Button>
        </div>
      </div>

      {/* Search & Category Filter */}
      <div className="flex items-center gap-4 mb-6 flex-wrap">
        <div className="relative w-72">
          <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input 
            placeholder="Search clients by name, email, phone..." 
            className="pl-10" 
            value={clientSearch} 
            onChange={(e) => setClientSearch(e.target.value)}
            data-testid="client-search-input"
          />
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button
            variant={categoryFilter === 'all' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setCategoryFilter('all')}
            className={categoryFilter === 'all' ? 'bg-slate-900' : ''}
          >
            All ({clients.length})
          </Button>
          {categories.map((cat) => {
            const count = clients.filter(c => c.category === cat).length;
            return (
              <Button
                key={cat}
                variant={categoryFilter === cat ? 'default' : 'outline'}
                size="sm"
                onClick={() => setCategoryFilter(cat)}
                className={categoryFilter === cat ? 'bg-slate-900' : ''}
              >
                <Tag className="w-3 h-3 mr-1" />
                {cat} ({count})
              </Button>
            );
          })}
        </div>
      </div>

      <div className="space-y-6">
        {filteredClients.map((client) => (
          <Card key={client.id} className="border-slate-200 shadow-sm" data-testid={`client-card-${client.id}`}>
            <CardHeader>
              <div className="flex justify-between items-start">
                <div className="flex items-start gap-4">
                  <div className="w-16 h-16 rounded-lg bg-slate-100 flex items-center justify-center overflow-hidden border border-slate-200">
                    {client.logo_url ? (
                      <img src={client.logo_url} alt={client.company_name} className="w-full h-full object-cover" />
                    ) : (
                      <Building2 className="w-8 h-8 text-slate-400" />
                    )}
                  </div>
                  <div>
                    <CardTitle className="text-xl flex items-center gap-2">
                      {client.company_name}
                      {client.client_code && (
                        <Badge variant="outline" className="bg-indigo-50 text-indigo-700 border-indigo-200 text-xs font-mono">
                          Code: {client.client_code}
                        </Badge>
                      )}
                      <Badge className={getCategoryColor(client.category)} variant="outline">
                        <Tag className="w-3 h-3 mr-1" />
                        {client.category || 'Uncategorized'}
                      </Badge>
                      <Badge variant="outline" className="text-xs font-normal">
                        {client.verification_type === 'pre_employment' ? 'Pre-Employment' : 
                         client.verification_type === 'post_employment' ? 'Post-Employment' : 'Pre & Post'}
                      </Badge>
                    </CardTitle>
                    <p className="text-sm text-slate-600 mt-1">
                      Contact: {client.contact_name} | {client.email} | {client.phone}
                    </p>
                    <div className="flex gap-2 mt-2">
                      {client.pre_employment_basic_first && (
                        <Badge className="bg-amber-100 text-amber-700 text-xs">Basic First</Badge>
                      )}
                      {client.client_approval_required && (
                        <Badge className="bg-blue-100 text-blue-700 text-xs">Client Approval</Badge>
                      )}
                      {client.agreement_start_date && (
                        <Badge variant="outline" className="text-xs">
                          <Calendar className="w-3 h-3 mr-1" />
                          {client.agreement_start_date} - {client.agreement_end_date || 'Ongoing'}
                        </Badge>
                      )}
                      {/* Status Badge */}
                      <Badge className={client.is_active !== false ? 'bg-green-500' : 'bg-gray-400'}>
                        {client.is_active !== false ? 'Active' : 'Inactive'}
                      </Badge>
                    </div>
                  </div>
                </div>
                <div className="flex gap-2">
                  {/* Toggle Status Button */}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleToggleClientStatus(client)}
                    className={client.is_active !== false ? 'border-amber-300 text-amber-600 hover:bg-amber-50' : 'border-green-300 text-green-600 hover:bg-green-50'}
                    title={client.is_active !== false ? 'Deactivate Client' : 'Activate Client'}
                  >
                    {client.is_active !== false ? <PowerOff className="w-4 h-4" /> : <Power className="w-4 h-4" />}
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setShowEmailTemplates(client)} className="border-slate-300">
                    <Mail className="w-4 h-4 mr-2" />
                    Templates
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => openManageUsers(client)} className="border-slate-300">
                    <Users className="w-4 h-4 mr-2" />
                    Users
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => openEditClient(client)} className="border-slate-300">
                    <Edit className="w-4 h-4 mr-2" />
                    Edit
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setSelectedClient(client.id);
                      setNewEntity({ ...newEntity, client_id: client.id });
                      setShowAddEntity(true);
                    }}
                    className="border-slate-300"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Entity
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDeleteClient(client)}
                    className="border-red-200 text-red-500 hover:bg-red-50 hover:text-red-700"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {getClientEntities(client.id).length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Entity Name</TableHead>
                      <TableHead>Code</TableHead>
                      <TableHead>Location</TableHead>
                      <TableHead>HR User(s)</TableHead>
                      <TableHead>Package</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {getClientEntities(client.id).map((entity) => {
                      const users = getEntityUsersList(entity.id);
                      // Fetch users if not loaded
                      if (!entityUsers[entity.id]) {
                        fetchEntityUsers(entity.id);
                      }
                      return (
                        <TableRow key={entity.id}>
                          <TableCell className="font-medium">{entity.entity_name}</TableCell>
                          <TableCell>
                            {entity.entity_code ? (
                              <Badge variant="outline" className="bg-indigo-50 text-indigo-700 border-indigo-200 text-xs font-mono">
                                {entity.entity_code}
                              </Badge>
                            ) : '-'}
                          </TableCell>
                          <TableCell>{entity.location || 'N/A'}</TableCell>
                          <TableCell>
                            {users.length > 0 ? (
                              <div className="space-y-1">
                                {users.map(user => (
                                  <div key={user.id} className={`flex items-center justify-between gap-2 p-1.5 rounded text-xs ${user.is_active !== false ? 'bg-slate-50' : 'bg-gray-100 opacity-60'}`}>
                                    <div>
                                      <p className={`font-medium ${user.is_active !== false ? 'text-slate-800' : 'text-gray-500 line-through'}`}>{user.name}</p>
                                      <p className="text-slate-500">{user.email}</p>
                                      {user.is_active === false && <Badge variant="outline" className="text-[10px] text-gray-500 mt-0.5">Inactive</Badge>}
                                    </div>
                                    <div className="flex gap-1">
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-6 w-6 p-0"
                                        onClick={() => {
                                          setEditingUser({ ...user, entity_id: entity.id });
                                          setShowEditEntityUser(entity.id);
                                        }}
                                        title="Edit User"
                                      >
                                        <Edit2 className="h-3 w-3" />
                                      </Button>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-6 w-6 p-0 text-blue-500 hover:text-blue-700"
                                        onClick={() => handleResetUserPassword(user, entity.id)}
                                        title="Reset Password"
                                      >
                                        <KeyRound className="h-3 w-3" />
                                      </Button>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className={`h-6 w-6 p-0 ${user.is_active !== false ? 'text-amber-500 hover:text-amber-700' : 'text-green-500 hover:text-green-700'}`}
                                        onClick={() => handleToggleUserStatus(user, entity.id)}
                                        title={user.is_active !== false ? 'Deactivate User' : 'Activate User'}
                                      >
                                        {user.is_active !== false ? <PowerOff className="h-3 w-3" /> : <Power className="h-3 w-3" />}
                                      </Button>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-6 w-6 p-0 text-red-500 hover:text-red-700"
                                        onClick={() => handleDeactivateEntityUser(user, entity.id)}
                                        title="Delete User"
                                      >
                                        <Trash2 className="h-3 w-3" />
                                      </Button>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <span className="text-slate-400 text-xs">No users assigned</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <div>
                              <span>{getPackageName(entity.package_id)}</span>
                              {entity.package_id && (
                                <div className="flex items-center gap-1 mt-0.5">
                                  <code className="text-[10px] text-slate-400 font-mono truncate max-w-[140px]" title={entity.package_id}>{entity.package_id}</code>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-4 w-4 p-0 text-slate-400 hover:text-blue-600"
                                    onClick={() => { navigator.clipboard.writeText(entity.package_id); toast.success('Package ID copied'); }}
                                    title="Copy Package ID"
                                  >
                                    <Copy className="h-3 w-3" />
                                  </Button>
                                </div>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge className={entity.is_active !== false ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-600'}>
                              {entity.is_active !== false ? 'Active' : 'Inactive'}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-2">
                              {/* Toggle Entity Status */}
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleToggleEntityStatus(entity)}
                                className={entity.is_active !== false ? 'text-amber-500 hover:text-amber-700' : 'text-green-500 hover:text-green-700'}
                                title={entity.is_active !== false ? 'Deactivate Entity' : 'Activate Entity'}
                              >
                                {entity.is_active !== false ? <PowerOff className="h-4 w-4" /> : <Power className="h-4 w-4" />}
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => openEditEntity(entity)}
                                className="text-blue-500 hover:text-blue-700 hover:bg-blue-50"
                                title="Edit Entity"
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  setSelectedClient(client);
                                  setSelectedEntityForUser(entity);
                                  fetchClientUsers(client.id);
                                  setShowAddUser(true);
                                }}
                              >
                                <UserPlus className="h-4 w-4 mr-1" />
                                Add User
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                onClick={() => handleDeleteEntity(entity, client.id)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              ) : (
                <p className="text-slate-500 text-center py-4">No entities added yet</p>
              )}
            </CardContent>
          </Card>
        ))}

        {filteredClients.length === 0 && (
          <Card className="border-slate-200 shadow-sm">
            <CardContent className="text-center py-12">
              <Building2 className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-600 mb-4">
                {clientSearch ? `No clients matching "${clientSearch}"` : categoryFilter === 'all' ? 'No clients added yet' : `No clients in "${categoryFilter}" category`}
              </p>
              <Button onClick={() => setShowAddClient(true)} className="bg-slate-900 hover:bg-slate-800">
                <Plus className="w-4 h-4 mr-2" />
                Add Your First Client
              </Button>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Add Client Dialog */}
      <Dialog open={showAddClient} onOpenChange={setShowAddClient}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add New Client</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddClient}>
            {renderClientFormFields(false)}
            <DialogFooter className="mt-6">
              <Button type="button" variant="outline" onClick={() => setShowAddClient(false)}>Cancel</Button>
              <Button type="submit" className="bg-slate-900 hover:bg-slate-800">Add Client</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Client Dialog */}
      <Dialog open={!!showEditClient} onOpenChange={(open) => !open && setShowEditClient(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Client</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdateClient}>
            {renderClientFormFields(true)}
            {/* Send Invitation Section */}
            {showEditClient && (() => {
              const hrUser = clientUsers.find(u => u.client_id === showEditClient.id && u.role === 'hr_admin');
              if (!hrUser) return null;
              // Compare the editable form values against the CLIENT's
              // stored Contact Name / Email (not against the HR user's
              // email, which is a different concept and would never
              // match the client contact — that bug used to keep the
              // Send Invitation button permanently disabled).
              // May 24, 2026 fix.
              const formEmail = (newClient.email || '').trim();
              const formName = (newClient.contact_name || '').trim();
              const savedEmail = (showEditClient.email || '').trim();
              const savedName = (showEditClient.contact_name || '').trim();
              const displayEmail = formEmail || savedEmail || hrUser.email;
              const hasUnsavedEmailChange = formEmail && formEmail.toLowerCase() !== savedEmail.toLowerCase();
              const hasUnsavedNameChange = formName && formName !== savedName;
              const hasUnsavedChanges = hasUnsavedEmailChange || hasUnsavedNameChange;
              return (
                <div className="border rounded-lg p-4 bg-blue-50 mt-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-slate-700">Portal Access Invitation</p>
                      <p className="text-xs text-slate-500">Send login credentials to {displayEmail}</p>
                      {hasUnsavedChanges && (
                        <p className="text-xs text-amber-700 mt-1">⚠ Save Changes first to sync Contact Name / Email before sending the invitation.</p>
                      )}
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      data-testid="send-client-invitation"
                      disabled={hasUnsavedChanges}
                      className="border-blue-300 text-blue-700 hover:bg-blue-100 disabled:opacity-50 disabled:cursor-not-allowed"
                      onClick={() => handleSendInvitation(hrUser.id, formName || hrUser.name || displayEmail)}
                    >
                      <Mail className="w-4 h-4 mr-1" /> Send Invitation
                    </Button>
                  </div>
                </div>
              );
            })()}
            <DialogFooter className="mt-6">
              <Button type="button" variant="outline" onClick={() => setShowEditClient(null)}>Cancel</Button>
              <Button type="submit" className="bg-slate-900 hover:bg-slate-800">Save Changes</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Entity Dialog */}
      <Dialog open={showAddEntity} onOpenChange={setShowAddEntity}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Add Entity</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddEntity} className="space-y-4">
            <div>
              <Label>Entity Name *</Label>
              <Input placeholder="e.g., Mumbai Office" value={newEntity.entity_name} onChange={(e) => setNewEntity({ ...newEntity, entity_name: e.target.value })} required />
            </div>
            <div>
              <Label>Package *</Label>
              <select value={newEntity.package_id} onChange={(e) => setNewEntity({ ...newEntity, package_id: e.target.value })} required className="w-full px-3 py-2 border border-slate-200 rounded-md">
                <option value="">Select Package</option>
                {packages.map(pkg => <option key={pkg.id} value={pkg.id}>{pkg.name} - ₹{pkg.price}</option>)}
              </select>
            </div>
            <div>
              <Label>Location</Label>
              <Input placeholder="e.g., Mumbai, India" value={newEntity.location} onChange={(e) => setNewEntity({ ...newEntity, location: e.target.value })} />
            </div>
            
            {/* Optional HR User Creation */}
            <div className="border rounded-lg p-4 bg-slate-50">
              <div className="flex items-center gap-2 mb-3">
                <input
                  type="checkbox"
                  id="create_hr_user"
                  checked={newEntity.create_hr_user}
                  onChange={(e) => setNewEntity({ ...newEntity, create_hr_user: e.target.checked })}
                  className="rounded border-slate-300"
                />
                <label htmlFor="create_hr_user" className="text-sm font-medium cursor-pointer">Create HR User for this Entity</label>
              </div>
              
              {newEntity.create_hr_user && (
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">HR Name *</Label>
                      <Input 
                        value={newEntity.hr_name} 
                        onChange={(e) => setNewEntity({ ...newEntity, hr_name: e.target.value })} 
                        placeholder="HR Admin Name"
                        required={newEntity.create_hr_user}
                      />
                    </div>
                    <div>
                      <Label className="text-xs">HR Email *</Label>
                      <Input 
                        type="email"
                        value={newEntity.hr_email} 
                        onChange={(e) => setNewEntity({ ...newEntity, hr_email: e.target.value })} 
                        placeholder="hr@company.com"
                        required={newEntity.create_hr_user}
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs">Password</Label>
                    <Input 
                      type="text"
                      value={newEntity.hr_password} 
                      onChange={(e) => setNewEntity({ ...newEntity, hr_password: e.target.value })} 
                      placeholder="Enter password or leave blank for default (hr123)"
                    />
                    <p className="text-xs text-slate-500 mt-1">Default password: hr123</p>
                  </div>
                  <div>
                    <Label className="text-sm">User Role</Label>
                    <Select value={newEntity.hr_role} onValueChange={(v) => setNewEntity({ ...newEntity, hr_role: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="client_user">Client User - Limited Access</SelectItem>
                        <SelectItem value="client_admin">Client Admin - Full Access</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowAddEntity(false)}>Cancel</Button>
              <Button type="submit" className="bg-slate-900 hover:bg-slate-800">Add Entity</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Manage Users Dialog */}
      <Dialog open={!!showManageUsers} onOpenChange={(open) => !open && setShowManageUsers(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              Manage Users - {showManageUsers?.company_name}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <p className="text-sm text-slate-600">Manage user access levels</p>
              <Button onClick={() => setShowAddUser(true)} size="sm" className="bg-slate-900 hover:bg-slate-800">
                <UserPlus className="w-4 h-4 mr-2" />
                Add User
              </Button>
            </div>

            <div className="p-4 bg-slate-50 rounded-lg space-y-2">
              <h4 className="font-medium text-sm">User Roles:</h4>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="flex items-start gap-2">
                  <Shield className="w-4 h-4 text-indigo-600 mt-0.5" />
                  <div>
                    <p className="font-medium">Client Admin</p>
                    <p className="text-slate-500 text-xs">Full access to all client data</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <User className="w-4 h-4 text-slate-600 mt-0.5" />
                  <div>
                    <p className="font-medium">Client User</p>
                    <p className="text-slate-500 text-xs">Access only to assigned entities</p>
                  </div>
                </div>
              </div>
            </div>

            {clientUsers.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Assigned Entities</TableHead>
                    <TableHead className="text-center">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {clientUsers.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell className="font-medium">{user.name}</TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>
                        <Badge className={user.client_role === 'client_admin' ? 'bg-indigo-100 text-indigo-700' : 'bg-slate-100 text-slate-700'}>
                          {user.client_role === 'client_admin' ? <><Shield className="w-3 h-3 mr-1" />Admin</> : <><User className="w-3 h-3 mr-1" />User</>}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {user.client_role === 'client_admin' ? (
                          <span className="text-slate-500 text-sm">All Entities</span>
                        ) : user.assigned_entities?.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {user.assigned_entities.slice(0, 2).map(entityId => (
                              <Badge key={entityId} variant="outline" className="text-xs">{getEntityName(entityId)}</Badge>
                            ))}
                            {user.assigned_entities.length > 2 && <Badge variant="outline" className="text-xs">+{user.assigned_entities.length - 2}</Badge>}
                          </div>
                        ) : (
                          <span className="text-slate-400 text-sm">None assigned</span>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setEditingUser({
                              id: user.id,
                              name: user.name || '',
                              email: user.email || '',
                              phone: user.phone || '',
                              client_role: user.client_role || 'client_user',
                              assigned_entities: user.assigned_entities || [],
                            });
                            setShowEditClientUser(true);
                          }}
                          data-testid={`edit-client-user-${user.id}`}
                          title="Edit user"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-8">
                <Users className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">No users added yet</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Client User Dialog (from Manage Users) */}
      <Dialog
        open={showEditClientUser}
        onOpenChange={(open) => {
          if (!open) {
            setShowEditClientUser(false);
            setEditingUser(null);
          }
        }}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5" />
              Edit User{editingUser?.name ? ` - ${editingUser.name}` : ''}
            </DialogTitle>
          </DialogHeader>
          {editingUser && (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleUpdateClientUser();
              }}
              className="space-y-4"
            >
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Name *</Label>
                  <Input
                    value={editingUser.name}
                    onChange={(e) => setEditingUser({ ...editingUser, name: e.target.value })}
                    required
                    data-testid="edit-client-user-name"
                  />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={editingUser.email}
                    disabled
                    className="bg-slate-50"
                    data-testid="edit-client-user-email"
                  />
                  <p className="text-xs text-slate-500 mt-1">Email cannot be changed</p>
                </div>
              </div>
              <div>
                <Label>Phone</Label>
                <Input
                  value={editingUser.phone}
                  onChange={(e) => setEditingUser({ ...editingUser, phone: e.target.value })}
                  data-testid="edit-client-user-phone"
                />
              </div>
              <div>
                <Label>User Role *</Label>
                <Select
                  value={editingUser.client_role}
                  onValueChange={(v) => setEditingUser({ ...editingUser, client_role: v })}
                >
                  <SelectTrigger data-testid="edit-client-user-role">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="client_admin">Client Admin - Full Access</SelectItem>
                    <SelectItem value="client_user">Client User - Limited Access</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {editingUser.client_role === 'client_user' && showManageUsers && (
                <div>
                  <Label>Assigned Entities</Label>
                  <div className="mt-2 border rounded-lg p-3 max-h-40 overflow-y-auto space-y-2">
                    {getClientEntities(showManageUsers.id).map((entity) => (
                      <label key={entity.id} className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={editingUser.assigned_entities.includes(entity.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setEditingUser({
                                ...editingUser,
                                assigned_entities: [...editingUser.assigned_entities, entity.id],
                              });
                            } else {
                              setEditingUser({
                                ...editingUser,
                                assigned_entities: editingUser.assigned_entities.filter((id) => id !== entity.id),
                              });
                            }
                          }}
                          className="rounded border-slate-300"
                          data-testid={`edit-client-user-entity-${entity.id}`}
                        />
                        <span className="text-sm">{entity.entity_name}</span>
                      </label>
                    ))}
                    {getClientEntities(showManageUsers.id).length === 0 && (
                      <p className="text-sm text-slate-500">No entities available</p>
                    )}
                  </div>
                </div>
              )}
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowEditClientUser(false);
                    setEditingUser(null);
                  }}
                  data-testid="edit-client-user-cancel"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="bg-slate-900 hover:bg-slate-800"
                  data-testid="edit-client-user-save"
                >
                  Save Changes
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Add User Dialog */}
      <Dialog open={showAddUser} onOpenChange={(open) => {
        setShowAddUser(open);
        if (!open) {
          setSelectedEntityForUser(null);
          setNewUser({ name: '', email: '', phone: '', client_role: 'client_user', assigned_entities: [], password: '', send_invite: true, force_password_change: true });
        }
      }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              Add New User
              {selectedEntityForUser && (
                <span className="text-sm font-normal text-slate-500 block mt-1">
                  For entity: {selectedEntityForUser.entity_name} ({selectedClient?.company_name})
                </span>
              )}
              {showManageUsers && !selectedEntityForUser && (
                <span className="text-sm font-normal text-slate-500 block mt-1">
                  For client: {showManageUsers.company_name}
                </span>
              )}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddUser} className="space-y-4">
            {/* Option to assign existing client user when adding to entity */}
            {selectedEntityForUser && clientUsers.length > 0 && (
              <div className="border rounded-lg p-3 bg-amber-50 space-y-2">
                <Label className="text-sm font-medium text-amber-800">Assign Existing User to This Entity</Label>
                <div className="flex gap-2">
                  <Select onValueChange={(userId) => {
                    const existing = clientUsers.find(u => u.id === userId);
                    if (existing) {
                      setNewUser({ ...newUser, name: existing.name, email: existing.email, phone: existing.phone || '', client_role: existing.client_role || 'client_admin' });
                    }
                  }}>
                    <SelectTrigger><SelectValue placeholder="Select existing user..." /></SelectTrigger>
                    <SelectContent>
                      {clientUsers.map(u => (
                        <SelectItem key={u.id} value={u.id}>{u.name} ({u.email})</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <p className="text-xs text-amber-600">Select to auto-fill, or create a new user below</p>
              </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Name *</Label>
                <Input value={newUser.name} onChange={(e) => setNewUser({ ...newUser, name: e.target.value })} required />
              </div>
              <div>
                <Label>Email *</Label>
                <Input type="email" value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} required />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Phone</Label>
                <Input value={newUser.phone} onChange={(e) => setNewUser({ ...newUser, phone: e.target.value })} />
              </div>
              <div>
                <Label>Password</Label>
                <Input 
                  type="text" 
                  value={newUser.password} 
                  onChange={(e) => setNewUser({ ...newUser, password: e.target.value })} 
                  placeholder="Enter password or leave blank for default (hr123)"
                />
                <p className="text-xs text-slate-500 mt-1">Default password: hr123</p>
              </div>
            </div>
            <div>
              <Label>User Role *</Label>
              <Select value={newUser.client_role} onValueChange={(v) => setNewUser({ ...newUser, client_role: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="client_admin">Client Admin - Full Access</SelectItem>
                  <SelectItem value="client_user">Client User - Limited Access</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Show entity selection only when NOT adding for a specific entity and from Manage Users */}
            {newUser.client_role === 'client_user' && showManageUsers && !selectedEntityForUser && (
              <div>
                <Label>Assign Entities</Label>
                <div className="mt-2 border rounded-lg p-3 max-h-40 overflow-y-auto space-y-2">
                  {getClientEntities(showManageUsers.id).map((entity) => (
                    <label key={entity.id} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={newUser.assigned_entities.includes(entity.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setNewUser({ ...newUser, assigned_entities: [...newUser.assigned_entities, entity.id] });
                          } else {
                            setNewUser({ ...newUser, assigned_entities: newUser.assigned_entities.filter(id => id !== entity.id) });
                          }
                        }}
                        className="rounded border-slate-300"
                      />
                      <span className="text-sm">{entity.entity_name}</span>
                    </label>
                  ))}
                  {getClientEntities(showManageUsers.id).length === 0 && <p className="text-sm text-slate-500">No entities available</p>}
                </div>
              </div>
            )}
            
            {/* Show info when adding for specific entity */}
            {selectedEntityForUser && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-sm text-blue-700">
                  This user will be automatically assigned to the <strong>{selectedEntityForUser.entity_name}</strong> entity.
                </p>
              </div>
            )}
            
            {/* Invite Options */}
            <div className="border rounded-lg p-3 space-y-3 bg-slate-50">
              <p className="text-sm font-medium text-slate-700">Invitation Options</p>
              <div className="flex items-center gap-2">
                <Checkbox
                  id="send-user-invite"
                  checked={newUser.send_invite !== false}
                  onCheckedChange={(v) => setNewUser({ ...newUser, send_invite: v })}
                />
                <Label htmlFor="send-user-invite" className="text-sm cursor-pointer flex items-center gap-1">
                  <Mail className="w-3 h-3 text-blue-600" />
                  Send invitation email with login credentials
                </Label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  id="force-password-change"
                  checked={newUser.force_password_change !== false}
                  onCheckedChange={(v) => setNewUser({ ...newUser, force_password_change: v })}
                />
                <Label htmlFor="force-password-change" className="text-sm cursor-pointer flex items-center gap-1">
                  <KeyRound className="w-3 h-3 text-amber-600" />
                  Force password change on first login
                </Label>
              </div>
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowAddUser(false)}>Cancel</Button>
              <Button type="submit" className="bg-slate-900 hover:bg-slate-800">Add User</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Email Templates Dialog */}
      <Dialog open={!!showEmailTemplates} onOpenChange={(open) => !open && setShowEmailTemplates(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5" />
              Email Settings - {showEmailTemplates?.company_name}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-sm text-blue-800">
                <strong>Note:</strong> Email templates are now managed globally in Admin Settings → Mail Templates.
              </p>
              <p className="text-xs text-blue-600 mt-2">
                Use the CC Settings in the client form to control which emails copy this client.
              </p>
            </div>
            
            <div className="space-y-2">
              <h4 className="font-medium text-sm">Current CC Settings for this Client:</h4>
              {EMAIL_TEMPLATE_TYPES.map((template) => (
                <div key={template.id} className="flex items-center justify-between p-2 bg-slate-50 rounded text-sm">
                  <div>
                    <span className="font-medium">{template.name}</span>
                    <span className="text-xs text-slate-500 ml-2">({template.frequency})</span>
                  </div>
                  <Badge variant={showEmailTemplates?.email_cc_settings?.[template.id] ? "default" : "outline"}>
                    {showEmailTemplates?.email_cc_settings?.[template.id] ? "CC Enabled" : "CC Disabled"}
                  </Badge>
                </div>
              ))}
            </div>
            
            <p className="text-xs text-slate-500">To modify CC settings, edit the client and update in the "Email CC Settings" section.</p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Category Dialog */}
      <Dialog open={showAddCategory} onOpenChange={setShowAddCategory}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Tag className="w-5 h-5" />
              Add New Category
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Category Name *</Label>
              <Input
                value={newCategoryName}
                onChange={(e) => setNewCategoryName(e.target.value)}
                placeholder="e.g., Insurance Verification"
              />
            </div>
            <div className="p-3 bg-slate-50 rounded-lg">
              <p className="text-xs text-slate-500 mb-2">Existing Categories:</p>
              <div className="flex flex-wrap gap-1">
                {categories.map((cat) => (
                  <Badge key={cat} variant="outline" className="text-xs">{cat}</Badge>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => { setShowAddCategory(false); setNewCategoryName(''); }}>Cancel</Button>
            <Button onClick={handleAddCategory} className="bg-slate-900 hover:bg-slate-800">Add Category</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Entity Dialog */}
      <Dialog open={showEditEntity} onOpenChange={(open) => { if (!open) { setShowEditEntity(false); setEditingEntity(null); } }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-blue-600" />
              Edit Entity
            </DialogTitle>
          </DialogHeader>
          {editingEntity && (
            <form onSubmit={handleUpdateEntity} className="space-y-4">
              <div>
                <Label>Entity Name *</Label>
                <Input
                  value={editingEntity.entity_name}
                  onChange={(e) => setEditingEntity({ ...editingEntity, entity_name: e.target.value })}
                  placeholder="e.g., Mumbai Office"
                  required
                />
              </div>
              <div>
                <Label>Location</Label>
                <Input
                  value={editingEntity.location}
                  onChange={(e) => setEditingEntity({ ...editingEntity, location: e.target.value })}
                  placeholder="e.g., Mumbai, Maharashtra"
                />
              </div>
              <div>
                <Label>Entity Code (for API integration)</Label>
                <Input
                  value={editingEntity.entity_code || ''}
                  onChange={(e) => setEditingEntity({ ...editingEntity, entity_code: e.target.value.toUpperCase() })}
                  placeholder="e.g., RTX-BLR"
                  className="font-mono"
                />
                <p className="text-xs text-slate-400 mt-1">Short code used in external API calls instead of UUID</p>
              </div>
              <div>
                <Label>Package</Label>
                <Select
                  value={editingEntity.package_id || ''}
                  onValueChange={(v) => setEditingEntity({ ...editingEntity, package_id: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select package" />
                  </SelectTrigger>
                  <SelectContent>
                    {packages.map(pkg => (
                      <SelectItem key={pkg.id} value={pkg.id}>{pkg.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Contact Person</Label>
                  <Input
                    value={editingEntity.contact_person}
                    onChange={(e) => setEditingEntity({ ...editingEntity, contact_person: e.target.value })}
                    placeholder="Entity contact name"
                  />
                </div>
                <div>
                  <Label>Contact Phone</Label>
                  <Input
                    value={editingEntity.contact_phone}
                    onChange={(e) => setEditingEntity({ ...editingEntity, contact_phone: e.target.value })}
                    placeholder="Contact phone"
                  />
                </div>
              </div>
              <div>
                <Label>Contact Email</Label>
                <Input
                  type="email"
                  value={editingEntity.contact_email}
                  onChange={(e) => setEditingEntity({ ...editingEntity, contact_email: e.target.value })}
                  placeholder="entity@company.com"
                />
              </div>
              {/* User Role for entity HR user */}
              {editingEntity._hr_user_id && (
                <div>
                  <Label>User Role</Label>
                  <Select value={editingEntity._hr_role || 'client_user'} onValueChange={(v) => setEditingEntity({ ...editingEntity, _hr_role: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="client_user">Client User - Limited Access</SelectItem>
                      <SelectItem value="client_admin">Client Admin - Full Access</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              {/* Send Portal Invitation */}
              {editingEntity._hr_user_id && (
                <div className="border rounded-lg p-4 bg-blue-50">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-slate-700">Portal Access Invitation</p>
                      <p className="text-xs text-slate-500">Send login credentials to {editingEntity.contact_email}</p>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      data-testid="send-entity-invitation"
                      className="border-blue-300 text-blue-700 hover:bg-blue-100"
                      onClick={() => handleSendInvitation(editingEntity._hr_user_id, editingEntity.contact_person || editingEntity.contact_email)}
                    >
                      <Mail className="w-4 h-4 mr-1" /> Send Invitation
                    </Button>
                  </div>
                </div>
              )}
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => { setShowEditEntity(false); setEditingEntity(null); }}>Cancel</Button>
                <Button type="submit" className="bg-blue-600 hover:bg-blue-700">Save Changes</Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Entity User Dialog */}
      <Dialog open={!!showEditEntityUser} onOpenChange={(open) => { if (!open) { setShowEditEntityUser(null); setEditingUser(null); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <User className="w-5 h-5" />
              Edit Entity User
            </DialogTitle>
          </DialogHeader>
          {editingUser && (
            <div className="space-y-4">
              <div>
                <Label>Name</Label>
                <Input
                  value={editingUser.name || ''}
                  onChange={(e) => setEditingUser({ ...editingUser, name: e.target.value })}
                />
              </div>
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={editingUser.email || ''}
                  onChange={(e) => setEditingUser({ ...editingUser, email: e.target.value })}
                />
              </div>
              <div>
                <Label>Phone</Label>
                <Input
                  value={editingUser.phone || ''}
                  onChange={(e) => setEditingUser({ ...editingUser, phone: e.target.value })}
                />
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-xs text-amber-800">
                  <strong>Note:</strong> Any data previously created by this user (employees, cases, etc.) will retain the original creator's name for audit purposes.
                </p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowEditEntityUser(null); setEditingUser(null); }}>Cancel</Button>
            <Button onClick={handleUpdateEntityUser} className="bg-slate-900 hover:bg-slate-800">Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Import Clients Dialog */}
      <Dialog open={showBulkImportDialog} onOpenChange={(open) => {
        setShowBulkImportDialog(open);
        if (!open) {
          setBulkImportFile(null);
          setBulkImportData([]);
          setBulkImportErrors([]);
        }
      }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="client-bulk-import-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5 text-slate-600" />
              Bulk Import Clients
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              Upload a CSV file to bulk import clients. Each client will get a default entity. Optionally include HR user details for automatic account creation.
            </p>
            
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={downloadClientTemplate} data-testid="download-client-template-btn">
                <Download className="w-4 h-4 mr-2" />
                Download Template
              </Button>
            </div>
            
            <div className="border-2 border-dashed border-slate-200 rounded-lg p-6 text-center">
              <Input
                type="file"
                accept=".csv"
                data-testid="client-import-file-input"
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) {
                    setBulkImportFile(file);
                    parseClientCSV(file);
                  }
                }}
                className="mb-2"
              />
              <p className="text-xs text-slate-500">Supported format: CSV</p>
            </div>
            
            {bulkImportFile && (
              <div className="bg-slate-50 rounded-lg p-3">
                <div className="flex items-center gap-2">
                  <FileUp className="w-5 h-5 text-slate-600" />
                  <span className="font-medium text-slate-800">{bulkImportFile.name}</span>
                  <Badge className="ml-auto">{bulkImportData.length} records</Badge>
                </div>
              </div>
            )}

            {/* Validation Errors */}
            {bulkImportErrors.length > 0 && (
              <div className="bg-rose-50 border border-rose-200 rounded-lg p-3">
                <p className="text-sm font-medium text-rose-800 mb-2 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  Validation Errors ({bulkImportErrors.length})
                </p>
                <div className="max-h-32 overflow-y-auto space-y-1">
                  {bulkImportErrors.map((error, idx) => (
                    <p key={idx} className="text-xs text-rose-600">
                      Row {error.row}: {error.errors?.join(', ') || 'Invalid data'}
                    </p>
                  ))}
                </div>
              </div>
            )}

            {/* Preview Table */}
            {bulkImportData.length > 0 && (
              <div className="border rounded-lg overflow-hidden">
                <div className="bg-slate-50 px-4 py-2 border-b">
                  <p className="text-sm font-medium">
                    Preview: {bulkImportData.filter(d => d.valid).length} valid / {bulkImportData.length} total
                  </p>
                </div>
                <div className="max-h-64 overflow-y-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead className="w-12">#</TableHead>
                        <TableHead>Company Name</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Contact</TableHead>
                        <TableHead>HR Email</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {bulkImportData.slice(0, 10).map((row, idx) => (
                        <TableRow key={idx} className={row.valid ? '' : 'bg-rose-50'}>
                          <TableCell className="text-slate-500">{idx + 1}</TableCell>
                          <TableCell className="font-medium">{row.company_name || '-'}</TableCell>
                          <TableCell className="text-sm">{row.email || '-'}</TableCell>
                          <TableCell className="text-sm">{row.contact_name || '-'}</TableCell>
                          <TableCell className="text-sm">{row.hr_email || '-'}</TableCell>
                          <TableCell>
                            {row.valid ? (
                              <Badge className="bg-green-100 text-green-700">Valid</Badge>
                            ) : (
                              <Badge className="bg-rose-100 text-rose-700">Invalid</Badge>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  {bulkImportData.length > 10 && (
                    <p className="text-xs text-slate-500 text-center py-2">
                      Showing 10 of {bulkImportData.length} records...
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Send Invites Checkbox */}
            {bulkImportData.filter(d => d.valid).length > 0 && (
              <div className="flex items-center gap-2 py-2">
                <Checkbox
                  id="send-client-invites"
                  checked={sendClientInvites}
                  onCheckedChange={(checked) => setSendClientInvites(checked)}
                  data-testid="send-client-invites-checkbox"
                />
                <Label htmlFor="send-client-invites" className="text-sm cursor-pointer">
                  Send invitation emails to HR contacts with login credentials
                </Label>
              </div>
            )}
          </div>

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => {
              setShowBulkImportDialog(false);
              setBulkImportFile(null);
              setBulkImportData([]);
              setBulkImportErrors([]);
            }}>
              Cancel
            </Button>
            <Button 
              onClick={handleBulkImportClients}
              className="bg-slate-900 hover:bg-slate-800"
              disabled={bulkImportData.filter(d => d.valid).length === 0}
              data-testid="confirm-client-import-btn"
            >
              <Upload className="w-4 h-4 mr-2" />
              Import {bulkImportData.filter(d => d.valid).length} Clients
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
